/**
 * F 157 — the `setting` entity.
 *
 * Doctrine §5: if two lanes need the same primitive, it goes in FCr. Every FC
 * instance has settings. They were living in ad-hoc places — env vars, config
 * files, hard-coded defaults, scattered localStorage — which meant no lane
 * could see another's settings, none were auditable, and none were syncable.
 *
 * A setting is an ENTITY, not a config entry. That is the whole point: it gets
 * an id, an `fcid://`, containment via `parentId`, ordering via `order`
 * (F 156), the same default-deny permission model (F 156), and the same
 * peer-to-peer backup story as every other asset.
 *
 * WHY THE SCOPE EXISTS
 * --------------------
 * A key like `editor.fontSize` has four legitimate answers at once — one for the
 * operator, one per app, one per user, one per instance. Flattening those into
 * one value means the last writer wins and the operator cannot set a floor. So a
 * setting is (key, scope, scopeId), and resolution walks from most specific to
 * least.
 *
 * `BaseEntity` supplies id / parentId / order / timestamps. It has no `path` or
 * `label` field — the dispatch assumed it did — so `label` is declared here on
 * the kind, and `path` is derived from containment rather than stored.
 */

import type { BaseEntity } from './BaseEntity';

export type SettingScope = 'global' | 'instance' | 'app' | 'user';

export type SettingValueType = 'string' | 'number' | 'boolean' | 'json' | 'date';

export const SETTING_SCOPES: readonly SettingScope[] = ['global', 'instance', 'app', 'user'];
export const SETTING_VALUE_TYPES: readonly SettingValueType[] = ['string', 'number', 'boolean', 'json', 'date'];

/**
 * THE RESOLUTION ORDER — most specific first.
 *
 * user > app > instance > global
 *
 * Read the list as "try these in order, first hit wins, then the entity's own
 * `default`". `getSetting` below implements exactly this and nothing else, so
 * the order exists in one place rather than at each call site.
 */
export const SCOPE_RESOLUTION_ORDER: readonly SettingScope[] = ['user', 'app', 'instance', 'global'];

/** Scope-specificity rank. Higher = more specific. */
export function scopeRank(scope: SettingScope): number {
  const i = SCOPE_RESOLUTION_ORDER.indexOf(scope);
  return i === -1 ? -1 : SCOPE_RESOLUTION_ORDER.length - i;
}

/** A scope that MUST name what it applies to. `global` must NOT. */
export function scopeRequiresId(scope: SettingScope): boolean {
  return scope !== 'global';
}

export interface Setting extends BaseEntity {
  /** Canonical reference, so a setting is a node in the graph like any other. */
  fcid: string;
  /** The setting name. Unique within (scope, scopeId). */
  key: string;
  /** Human label. Declared here: BaseEntity has no `label`. */
  label: string;
  /** The stored value. Undefined means "not set — fall back to `default`". */
  value?: unknown;
  valueType: SettingValueType;
  scope: SettingScope;
  /** The app id, user id, or instance id. FORBIDDEN for `global`. */
  scopeId?: string | null;
  /** Fallback when no scope resolves. */
  default?: unknown;
  description?: string;
  /** Whether the UI may edit it. Default true. A locked setting is readable. */
  editable?: boolean;
}

export const SETTING_FCID_PREFIX = 'fcid://setting/';

export function settingFcid(id: string): string {
  return `${SETTING_FCID_PREFIX}${id}`;
}

export function isSetting(value: unknown): value is Setting {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as Setting).fcid === 'string' &&
    (value as Setting).fcid.startsWith(SETTING_FCID_PREFIX)
  );
}

/** Stable id for a (key, scope, scopeId) triple. */
export function settingId(key: string, scope: SettingScope, scopeId?: string | null): string {
  const scopePart = scopeId ? `${scope}:${scopeId}` : scope;
  return `${scopePart}::${key}`;
}

// ── value-type validation ──────────────────────────────────────────────────

/**
 * Does `value` satisfy `declared`?
 *
 * Deliberately STRICT. A setting typed `number` that receives `"42"` is a bug
 * in the caller, and coercing it would hide the bug until the value was read
 * back somewhere else. `json` accepts anything except `undefined`; `date` takes
 * a string or a Date that PARSES — an invalid date string is rejected rather
 * than stored as truthy garbage.
 */
export function valueMatchesType(value: unknown, declared: SettingValueType): boolean {
  if (value === undefined || value === null) return true; // "unset" is always allowed
  switch (declared) {
    case 'string':  return typeof value === 'string';
    case 'number':  return typeof value === 'number' && Number.isFinite(value);
    case 'boolean': return typeof value === 'boolean';
    case 'json':    return true;
    case 'date':
      if (value instanceof Date) return !Number.isNaN(value.getTime());
      if (typeof value === 'string') return !Number.isNaN(Date.parse(value));
      return typeof value === 'number' && Number.isFinite(value);
    default: return false;
  }
}

/** A human name for the declared type, used in refusal messages. */
export function describeType(t: SettingValueType): string {
  return t === 'json' ? 'json' : String(t);
}